import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { useAuth } from '@/hooks/useAuth';
import { getTimeUntilTarget } from '@/hooks/useCountdown';
import { useDailySpinOffchain } from '@/hooks/useDailySpinOffchain';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { PrizeDetails, useRaffle } from '@/hooks/useRaffle';
import { PlumeRaffleUserPrizeData } from '@/types';
import { formatCurrencyToLocale, formatNumberToLocale } from '@/utils';
import { Box, Card, Flex, HStack, Heading, Icon, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { PiQuestion } from 'react-icons/pi';
import { AddTicketsModal } from './AddTicketsModal';
import { RaffleFaqModal } from './RaffleFaqModal';
import { RafflePrizeCardSkeleton } from './Skeletons';

type RafflePrize = PrizeDetails & { yourTickets: number | null; isWon: boolean; id: number; endsAt: Date | undefined };

const getImgPathFromPrizeId = (prizeId: number) => `/assets/daily-spin/raffle-prizes/${prizeId}.avif`;

export const Raffle = ({ raffleUserPrizeData }: { raffleUserPrizeData: PlumeRaffleUserPrizeData[] }) => {
  const { raffleTicketsBalance, isLoadingDailySpinStats, refetchDailySpinStats } = useDailySpinOnchain();
  const { isAuthenticated } = useAuth();
  const [numTicketsToSubmit, setNumTicketsToSubmit] = useState(0);
  const [isRaffleFaqModalOpen, setIsRaffleFaqModalOpen] = useState(false);
  const { refetch: refetchSpinHistory, isLoading: isLoadingDailySpinOffchain } = useDailySpinOffchain();

  const { prizesData, isLoadingPrizes, refetchRaffle } = useRaffle();

  const refreshRaffleData = useCallback(() => {
    refetchRaffle();
    refetchDailySpinStats?.();
    refetchSpinHistory?.();

    // in case indexing is lagging, refetch spin history again
    setTimeout(() => {
      refetchSpinHistory?.();
    }, 1500);
  }, [refetchRaffle, refetchDailySpinStats, refetchSpinHistory]);

  const rafflePrizes = useMemo(() => {
    if (!prizesData) return [] as RafflePrize[];

    // If user is not authenticated, return prizes without user-specific data
    if (!isAuthenticated || !raffleUserPrizeData) {
      return prizesData.map((prize, index) => {
        const prizeId = index + 1;
        return {
          ...prize,
          id: prizeId,
          yourTickets: null,
          isWon: false,
          endsAt:
            prize.endTimestamp && prize.endTimestamp > 0n ? new Date(Number(prize.endTimestamp) * 1000) : undefined,
        };
      });
    }

    return prizesData.map((prize, index) => {
      const userPrizeData = raffleUserPrizeData.find((data) => data.prizeId === Number(index + 1));
      const prizeId = index + 1; // 1 based index onchain for Raffle prizes
      return {
        ...prize,
        id: prizeId,
        value: Number(prize.value),
        totalTickets: Number(prize.totalTickets),
        winnerIndex: Number(prize.winnerIndex),
        totalUsers: Number(prize.totalUsers),
        yourTickets: userPrizeData?.ticketsEntered || null,
        isWon: userPrizeData?.isPrizeWinner || false,
        endsAt: prize.endTimestamp && prize.endTimestamp > 0n ? new Date(Number(prize.endTimestamp) * 1000) : undefined,
      };
    });
  }, [prizesData, raffleUserPrizeData, isAuthenticated]);

  const ongoingRafflePrizes = rafflePrizes.filter((prize) => prize.isActive);
  const endedRafflePrizes = rafflePrizes.filter((prize) => !prize.isActive);

  return (
    <VStack alignItems="start" w="full" gap={4}>
      <Flex direction={['column', 'row']} gap={[4, 2]} justifyContent="space-between" w="full">
        <VStack alignItems="start">
          <HStack alignItems="center">
            <Heading size="3xl">Raffle</Heading>
            {isAuthenticated && (
              <RoundedBadge py={1.5} px={4} bg="#F3F1FF">
                <Image src="/assets/daily-spin/raffle-ticket.svg" />
                {isLoadingDailySpinStats ? <Spinner /> : formatNumberToLocale(raffleTicketsBalance)} Raffle Ticket
                {raffleTicketsBalance === 1 ? '' : 's'}
              </RoundedBadge>
            )}
          </HStack>
          <Heading size="lg" fontWeight="500" color="fg.muted">
            Use your tickets in raffles for a chance to win RWA prizes
          </Heading>
        </VStack>
        <RoundedButton variant="outline" onClick={() => setIsRaffleFaqModalOpen(true)}>
          <Icon as={PiQuestion} boxSize={4} />
          Raffle FAQ
        </RoundedButton>
        <RaffleFaqModal isOpen={isRaffleFaqModalOpen} onClose={() => setIsRaffleFaqModalOpen(false)} />
      </Flex>
      {ongoingRafflePrizes && ongoingRafflePrizes.length > 0 && <Heading size="2xl">Ongoing</Heading>}
      <RafflePrizesList
        isLoading={isLoadingPrizes}
        isLoadingDailySpinOffchain={isLoadingDailySpinOffchain}
        raffleTicketsBalance={raffleTicketsBalance}
        rafflePrizes={ongoingRafflePrizes}
        numTicketsToSubmit={numTicketsToSubmit}
        setNumTicketsToSubmit={setNumTicketsToSubmit}
        onRefresh={refreshRaffleData}
        placeholder="No ongoing raffles, check back later to see what prizes are in store"
        isAuthenticated={isAuthenticated}
      />
      {endedRafflePrizes && endedRafflePrizes.length > 0 && (
        <>
          <Heading size="2xl">Ended</Heading>
          <RafflePrizesList
            isLoading={isLoadingPrizes}
            isLoadingDailySpinOffchain={isLoadingDailySpinOffchain}
            raffleTicketsBalance={raffleTicketsBalance}
            rafflePrizes={endedRafflePrizes}
            numTicketsToSubmit={numTicketsToSubmit}
            setNumTicketsToSubmit={setNumTicketsToSubmit}
            onRefresh={refreshRaffleData}
            placeholder="All raffles are still ongoing! Bid your tickets now to be the first to win a prize"
            isAuthenticated={isAuthenticated}
          />
        </>
      )}
    </VStack>
  );
};

const RaffleStat = ({
  title,
  value,
  loading = false,
}: {
  title: string;
  value: number | string;
  loading?: boolean;
}) => (
  <Box maxW={150} flex="1">
    <Text color="fg.muted" fontWeight="500">
      {title}
    </Text>
    {loading ? <Spinner /> : <Heading size="md">{value}</Heading>}
  </Box>
);

const RafflePrizesList = ({
  rafflePrizes,
  isLoading,
  isLoadingDailySpinOffchain,
  numTicketsToSubmit,
  setNumTicketsToSubmit,
  raffleTicketsBalance,
  placeholder,
  onRefresh,
  isAuthenticated,
}: {
  rafflePrizes: RafflePrize[];
  isLoading: boolean;
  numTicketsToSubmit: number;
  setNumTicketsToSubmit: (n: number) => void;
  raffleTicketsBalance: number;
  placeholder: string;
  onRefresh: () => void;
  isAuthenticated: boolean;
  isLoadingDailySpinOffchain: boolean;
}) => {
  if (isLoading) return <RafflePrizeCardSkeleton />;
  if (rafflePrizes.length)
    return (
      <>
        {rafflePrizes.map((rafflePrize, prizeId) => (
          <RafflePrizeCard
            key={prizeId}
            rafflePrize={rafflePrize}
            prizeId={prizeId}
            ticketsAvailable={raffleTicketsBalance}
            numTicketsToSubmit={numTicketsToSubmit}
            setNumTicketsToSubmit={setNumTicketsToSubmit}
            onRefresh={onRefresh}
            isAuthenticated={isAuthenticated}
            isLoadingUserTickets={isLoadingDailySpinOffchain}
          />
        ))}
      </>
    );
  return placeholder;
};

const RafflePrizeCard = ({
  rafflePrize,
  prizeId,
  ticketsAvailable,
  numTicketsToSubmit,
  setNumTicketsToSubmit,
  onRefresh,
  isAuthenticated,
  isLoadingUserTickets,
}: {
  rafflePrize: RafflePrize;
  prizeId: number;
  ticketsAvailable: number;
  numTicketsToSubmit: number;
  setNumTicketsToSubmit: (n: number) => void;
  onRefresh: () => void;
  isAuthenticated: boolean;
  isLoadingUserTickets: boolean;
}) => {
  const { name, description, value, totalTickets: tickets, yourTickets, endsAt } = rafflePrize;
  const ended = endsAt ? endsAt.getTime() < Date.now() : false;

  return (
    <>
      <Card.Root w="full" borderRadius="8px" overflow="hidden" hideFrom="md">
        <Card.Body p={6}>
          <VStack alignItems="start" gap={4}>
            <HStack gap={4}>
              <Box
                w="90px"
                rounded="16px"
                h="90px"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                bg="white"
                flexShrink={0}
              >
                <Image src={getImgPathFromPrizeId(prizeId)} w="100%" h="100%" objectFit="contain" />
              </Box>
              <Heading fontSize="lg">{name}</Heading>
            </HStack>
            <Text fontWeight="500" color="text.secondary">
              {description}
            </Text>
            <VStack alignItems="start" gap={3} w="full">
              <HStack justifyContent="space-between" w="full">
                <Text fontWeight="500" color="text.secondary">
                  Value
                </Text>
                <Heading fontSize="lg" fontWeight="500">
                  {formatCurrencyToLocale(value).replace('.00', '')}
                </Heading>
              </HStack>
              <HStack justifyContent="space-between" w="full">
                <Text fontWeight="500" color="text.secondary">
                  Tickets
                </Text>
                <Heading fontSize="lg" fontWeight="500">
                  {formatNumberToLocale(tickets)}
                </Heading>
              </HStack>
              {isAuthenticated && (
                <HStack justifyContent="space-between" w="full">
                  <Text fontWeight="500" color="text.secondary">
                    Your Tickets
                  </Text>
                  <Heading fontSize="lg" fontWeight="500">
                    {formatNumberToLocale(yourTickets || 0)}
                  </Heading>
                </HStack>
              )}
              {endsAt && !ended && (
                <HStack justifyContent="space-between" w="full">
                  <Text fontWeight="500" color="text.secondary">
                    Ending in
                  </Text>
                  <Heading fontSize="lg" fontWeight="500">
                    {getTimeUntilTarget(endsAt)}
                  </Heading>
                </HStack>
              )}
              {isAuthenticated && (
                <Box w="full">
                  <AddTicketsModal
                    name={name}
                    prizeId={prizeId}
                    numOfTicketsToSubmit={numTicketsToSubmit}
                    setNumOfTicketsToSubmit={setNumTicketsToSubmit}
                    ticketsAvailable={ticketsAvailable}
                    onSuccess={() => {
                      onRefresh();
                    }}
                    fullWidth
                  />
                </Box>
              )}
            </VStack>
          </VStack>
        </Card.Body>
      </Card.Root>
      <Card.Root w="full" borderRadius="2xl" overflow="hidden" hideBelow="md">
        <HStack h="full" gap={0}>
          <Box
            w="226px"
            h="226px"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            bg="white"
            flexShrink={0}
          >
            <Image src={getImgPathFromPrizeId(prizeId)} w="100%" h="100%" objectFit="contain" />
          </Box>
          <VStack alignSelf="stretch" w="full">
            <HStack
              alignSelf="stretch"
              justifyContent="space-between"
              bg="bg"
              flex="1"
              padding={6}
              borderBottom="1px solid #F0F0F0"
            >
              <Box>
                <Heading size="md">{name}</Heading>
                <Text color="fg.muted" fontWeight="500" maxW={['100%', '100%', '80%']}>
                  {description}
                </Text>
              </Box>
              {isAuthenticated && (
                <>
                  <AddTicketsModal
                    name={name}
                    prizeId={prizeId}
                    numOfTicketsToSubmit={numTicketsToSubmit}
                    setNumOfTicketsToSubmit={setNumTicketsToSubmit}
                    ticketsAvailable={ticketsAvailable}
                    onSuccess={() => {
                      onRefresh();
                    }}
                  />
                </>
              )}
            </HStack>
            <HStack alignSelf="stretch" justifyContent="space-between" flex="1" padding={6}>
              <HStack flex="1" justifyContent="start">
                <RaffleStat title="Value" value={formatCurrencyToLocale(value).replace('.00', '')} />
                <RaffleStat title="Tickets" value={formatNumberToLocale(tickets)} />
                {isAuthenticated && (
                  <RaffleStat
                    title="Your Tickets"
                    value={formatNumberToLocale(yourTickets || 0)}
                    loading={isLoadingUserTickets}
                  />
                )}
                {endsAt && !ended && <RaffleStat title="Ending in" value={getTimeUntilTarget(endsAt)} />}
              </HStack>
              {endsAt && (
                <RoundedBadge h={9} bgColor={ended ? 'bg.muted' : '#FFF6C7'} color={ended ? 'fg.muted' : '#963D01'}>
                  {ended ? 'Ended' : 'Ending Soon!'}
                </RoundedBadge>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Card.Root>
    </>
  );
};
